import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SEO } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL || "";

const S = {
  wrap: { background: "#0b1220", color: "#e5e7eb", minHeight: "100vh" },
  hero: { padding: "140px 20px 40px", textAlign: "center", maxWidth: 820, margin: "0 auto" },
  h1: { fontSize: 38, fontWeight: 800, color: "#ffffff", marginBottom: 12 },
  lead: { fontSize: 16, color: "#cbd5e1", lineHeight: 1.75 },
  section: { maxWidth: 820, margin: "0 auto", padding: "24px 20px" },
  h2: { fontSize: 22, fontWeight: 700, color: "#fdba74", marginBottom: 14 },
  card: { background: "#111a2e", border: "1px solid #1f2a44", borderRadius: 12, padding: 22, marginBottom: 16 },
  p: { fontSize: 15, lineHeight: 1.75, color: "#d1d5db", marginBottom: 10 },
  strong: { color: "#ffffff" },
  btn: { display: "inline-block", background: "#ea580c", color: "#fff", fontWeight: 700, padding: "12px 24px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 15 },
  ghost: { display: "inline-block", background: "transparent", color: "#fdba74", fontWeight: 600, padding: "10px 0", border: "none", cursor: "pointer", fontSize: 14 },
  chip: { display: "inline-block", background: "#1f2a44", color: "#cbd5e1", borderRadius: 999, padding: "4px 12px", fontSize: 12, marginRight: 8, marginBottom: 8 },
  input: { width: "100%", boxSizing: "border-box", border: "1px solid #334155", background: "#0b1220", color: "#e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, marginTop: 4 },
  label: { fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginTop: 12, display: "block" },
  note: { fontSize: 12, color: "#94a3b8", marginTop: 10, lineHeight: 1.6 },
  li: { fontSize: 14, lineHeight: 1.7, color: "#cbd5e1", marginBottom: 6 },
};

const UI = {
  en: {
    kicker: "CAREERS", title: "Careers at LAMKO",
    lead: "LAMKO Co., Ltd. develops and supplies high-purity specialty materials for the semiconductor, display and specialty chemical industries from our headquarters in Seoul. We are expanding our commercial team and welcome applications for the positions below.",
    open: "Open positions", details: "Responsibilities and requirements", apply: "Apply for this position",
    applyTitle: "Application", name: "Full name *", email: "Email *", phone: "Phone (optional)",
    cover: "Cover message (optional)", coverPh: "A brief note on your background and interest in the role.",
    cv: "CV * (PDF or Word, max 8 MB)", coverFile: "Cover letter (optional, PDF or Word)",
    materials: "Relevant materials (optional, PDF or Word) - portfolio, publications, work summary",
    submit: "Submit application", submitting: "Submitting...", cancel: "Cancel",
    note: "Applications are sent directly to our recruitment inbox. If you do not receive the confirmation email, please check your spam or junk folder.",
    successTitle: "Your application has been received.",
    successNote: "We will respond within one week. A confirmation email has been sent to your address. If it does not appear in your inbox, please check your spam or junk folder.",
    working: "Working at LAMKO",
    workingP1: "Our commercial team works directly with customers in the semiconductor and display industries and with our manufacturing and technical partners. The scope of each role is broad, and individual responsibility and contribution are visible from the first month.",
    workingP2: "We offer structured objectives, regular performance reviews, and compensation aligned with the Korean electronic materials market. Employment terms follow Korean labour law, including the four statutory social insurances.",
    process: "Application process",
    processText: "Submit your application through the form above, or email your CV to info@lamko.co.kr with the position title in the subject line. We review every application and respond within one week. The process consists of an introductory call followed by a role-specific interview.",
    spam: "If you do not receive our reply in your inbox, please check your spam or junk folder, as automated messages may be filtered by your email settings.",
    errName: "Please enter your name and a valid email address.",
    errCv: "Please attach your CV (PDF or Word).",
    errType: "Files must be PDF or Word (.pdf, .doc, .docx).",
    errSize: "Each file must be under 8 MB.",
    errFail: "Submission failed. Please email your application to info@lamko.co.kr.",
  },
  ko: {
    kicker: "채용", title: "LAMKO 채용 안내",
    lead: "LAMKO는 서울 본사에서 반도체·디스플레이·특수화학 산업을 위한 고순도 특수 소재를 개발·공급합니다. 영업 조직을 확대하며 아래 포지션에 대한 지원을 받고 있습니다.",
    open: "채용 중인 포지션", details: "담당 업무 및 자격 요건", apply: "지원하기",
    applyTitle: "지원서", name: "성명 *", email: "이메일 *", phone: "전화번호 (선택)",
    cover: "자기소개 (선택)", coverPh: "간단한 경력과 지원 동기를 적어 주세요.",
    cv: "이력서 * (PDF 또는 Word, 최대 8 MB)", coverFile: "커버레터 (선택, PDF 또는 Word)",
    materials: "관련 자료 (선택, PDF 또는 Word) - 포트폴리오, 논문, 경력기술서 등",
    submit: "지원서 제출", submitting: "제출 중...", cancel: "취소",
    note: "지원서는 채용 담당 메일로 직접 전송됩니다. 확인 메일이 오지 않으면 스팸함을 확인해 주세요.",
    successTitle: "지원서가 접수되었습니다.",
    successNote: "1주일 이내에 회신드리겠습니다. 확인 메일이 발송되었으며, 받은편지함에 없으면 스팸함을 확인해 주세요.",
    working: "LAMKO에서 일한다는 것",
    workingP1: "저희 영업 조직은 반도체·디스플레이 고객사, 그리고 제조·기술 파트너와 직접 협업합니다. 각 역할의 업무 범위가 넓고, 입사 첫 달부터 개인의 기여가 분명하게 드러납니다.",
    workingP2: "체계적인 목표 관리와 정기적인 성과 평가, 한국 전자재료 시장 수준에 맞춘 보상을 제공합니다. 고용 조건은 4대 보험을 포함하여 대한민국 노동법을 따릅니다.",
    process: "지원 절차",
    processText: "위 양식으로 지원하시거나, 제목에 포지션명을 기재하여 이력서를 info@lamko.co.kr 로 보내 주세요. 모든 지원서를 검토하며 1주일 이내에 회신드립니다. 전형은 인트로 콜과 직무 인터뷰로 진행됩니다.",
    spam: "회신이 받은편지함에 보이지 않으면 스팸함을 확인해 주세요. 자동 발송 메일은 메일 설정에 따라 필터링될 수 있습니다.",
    errName: "성명과 올바른 이메일을 입력해 주세요.",
    errCv: "이력서(PDF 또는 Word)를 첨부해 주세요.",
    errType: "파일은 PDF 또는 Word(.pdf, .doc, .docx) 형식이어야 합니다.",
    errSize: "각 파일은 8 MB 이하여야 합니다.",
    errFail: "제출에 실패했습니다. info@lamko.co.kr 로 이메일 지원해 주세요.",
  },
  zh: {
    kicker: "招聘", title: "LAMKO 招聘",
    lead: "LAMKO 总部位于首尔，为半导体、显示及特种化学行业开发并供应高纯度特种材料。我们正在扩充销售团队，欢迎申请以下职位。",
    open: "在招职位", details: "职责与要求", apply: "申请该职位",
    applyTitle: "申请", name: "姓名 *", email: "邮箱 *", phone: "电话（可选）",
    cover: "简要说明（可选）", coverPh: "请简要介绍您的背景与申请动机。",
    cv: "简历 *（PDF 或 Word，最大 8 MB）", coverFile: "求职信（可选，PDF 或 Word）",
    materials: "相关材料（可选，PDF 或 Word）— 作品集、论文、项目经历等",
    submit: "提交申请", submitting: "提交中...", cancel: "取消",
    note: "申请将直接发送至招聘邮箱。如未收到确认邮件，请检查垃圾邮件文件夹。",
    successTitle: "您的申请已收到。",
    successNote: "我们将在一周内回复。确认邮件已发送，如未在收件箱中，请检查垃圾邮件文件夹。",
    working: "在 LAMKO 工作",
    workingP1: "我们的销售团队直接对接半导体和显示行业客户以及制造与技术合作伙伴。每个岗位职责范围广，入职第一个月即可看到个人的贡献。",
    workingP2: "我们提供明确的目标管理、定期绩效评估，以及与韩国电子材料市场接轨的薪酬。雇佣条款遵循韩国劳动法，含四大社会保险。",
    process: "申请流程",
    processText: "请通过上方表单申请，或将简历发送至 info@lamko.co.kr（邮件主题注明职位名称）。我们会审阅每份申请并在一周内回复。流程包括初步电话沟通和职位面试。",
    spam: "如未在收件箱中看到我们的回复，请检查垃圾邮件文件夹。",
    errName: "请输入姓名和有效邮箱。",
    errCv: "请附上简历（PDF 或 Word）。",
    errType: "文件须为 PDF 或 Word（.pdf、.doc、.docx）。",
    errSize: "每个文件不得超过 8 MB。",
    errFail: "提交失败，请将申请发送至 info@lamko.co.kr。",
  },
  de: {
    kicker: "KARRIERE", title: "Karriere bei LAMKO",
    lead: "LAMKO Co., Ltd. entwickelt und liefert hochreine Spezialmaterialien für die Halbleiter-, Display- und Spezialchemieindustrie von unserem Hauptsitz in Seoul. Wir erweitern unser Vertriebsteam und freuen uns über Bewerbungen für die folgenden Positionen.",
    open: "Offene Stellen", details: "Aufgaben und Anforderungen", apply: "Jetzt bewerben",
    applyTitle: "Bewerbung", name: "Vollständiger Name *", email: "E-Mail *", phone: "Telefon (optional)",
    cover: "Kurze Nachricht (optional)", coverPh: "Eine kurze Notiz zu Ihrem Hintergrund und Ihrem Interesse an der Position.",
    cv: "Lebenslauf * (PDF oder Word, max. 8 MB)", coverFile: "Anschreiben (optional, PDF oder Word)",
    materials: "Weitere Unterlagen (optional, PDF oder Word) - Portfolio, Publikationen, Referenzen",
    submit: "Bewerbung absenden", submitting: "Wird gesendet...", cancel: "Abbrechen",
    note: "Bewerbungen gehen direkt an unser Recruiting-Postfach. Falls Sie keine Bestätigung erhalten, prüfen Sie bitte Ihren Spam-Ordner.",
    successTitle: "Ihre Bewerbung ist eingegangen.",
    successNote: "Wir antworten innerhalb einer Woche. Eine Bestätigung wurde an Ihre Adresse gesendet; prüfen Sie ggf. Ihren Spam-Ordner.",
    working: "Arbeiten bei LAMKO",
    workingP1: "Unser Vertriebsteam arbeitet direkt mit Kunden aus der Halbleiter- und Displayindustrie sowie mit unseren Fertigungs- und Technikpartnern. Der Aufgabenbereich jeder Position ist breit, und der eigene Beitrag ist vom ersten Monat an sichtbar.",
    workingP2: "Wir bieten klare Ziele, regelmäßige Leistungsbeurteilungen und eine Vergütung auf dem Niveau des koreanischen Elektronikmaterialmarkts. Die Beschäftigungsbedingungen folgen koreanischem Arbeitsrecht einschließlich der vier gesetzlichen Sozialversicherungen.",
    process: "Bewerbungsprozess",
    processText: "Bewerben Sie sich über das obige Formular oder senden Sie Ihren Lebenslauf mit der Positionsbezeichnung im Betreff an info@lamko.co.kr. Wir prüfen jede Bewerbung und antworten innerhalb einer Woche. Der Prozess besteht aus einem Kennenlerngespräch und einem positionsbezogenen Interview.",
    spam: "Falls unsere Antwort nicht in Ihrem Posteingang erscheint, prüfen Sie bitte Ihren Spam-Ordner.",
    errName: "Bitte geben Sie Ihren Namen und eine gültige E-Mail-Adresse ein.",
    errCv: "Bitte fügen Sie Ihren Lebenslauf bei (PDF oder Word).",
    errType: "Dateien müssen PDF oder Word sein (.pdf, .doc, .docx).",
    errSize: "Jede Datei darf höchstens 8 MB groß sein.",
    errFail: "Übermittlung fehlgeschlagen. Bitte senden Sie Ihre Bewerbung an info@lamko.co.kr.",
  },
};

const ROLES_EN = [
  {
    id: "Sales & Business Development Manager",
    title: "Sales & Business Development Manager",
    tag: "Seoul, Republic of Korea · Full-time",
    hook: "Lead commercial growth of LAMKO's electronic materials and display portfolio across Korea and international markets, including key-account and distributor management.",
    chips: ["5+ years, B2B specialty or electronic materials", "Native Korean, business English", "Korea and international travel"],
    more: [
      "Build and manage key-account and distributor relationships; develop new business in Korea, the wider APAC region, and North America.",
      "Lead new-product introduction and qualification with customers, coordinating samples, documentation and timelines with our supply and technical teams.",
      "Represent LAMKO at SEMICON, K-Display and international trade shows; develop market-entry strategies for new segments and regions.",
      "Oversee the distributor relationship for our cosmetic active ingredients line, with scope to take on additional product areas.",
      "Preferred: an established customer or distributor network; experience with dual-source and localization supply strategies; Japanese or Chinese language ability; an MBA or a technical or science degree.",
      "Compensation: competitive salary commensurate with experience, performance bonus, and the four statutory social insurances. Vehicle fuel costs reimbursed.",
    ],
  },
  {
    id: "Sales Executive",
    title: "Sales Executive",
    tag: "Seoul, Republic of Korea · Full-time",
    hook: "Manage day-to-day sales and customer support for the Korean market, reporting to the Sales & Business Development Manager.",
    chips: ["3+ years, B2B sales", "Native Korean required; English not required", "Seoul, with domestic travel"],
    more: [
      "Handle daily customer contact in Korean: enquiries, quotations, follow-up, and dependable service to existing accounts.",
      "Follow up on new leads and inbound website enquiries and support their conversion into orders.",
      "Coordinate samples, documentation and delivery timelines between customers and the internal team.",
      "Support customer visits, exhibitions and industry events in Korea, with weekly progress reporting.",
      "Preferred: an existing customer base or distributor contacts in specialty or electronic materials; a technical or science background.",
      "Compensation: KRW 35 to 40 million per year, commensurate with experience, plus the four statutory social insurances.",
    ],
  },
];

const ROLES_KO = [
  {
    id: "Sales & Business Development Manager",
    title: "영업·사업개발 매니저 (Sales & Business Development Manager)",
    tag: "서울 · 정규직",
    hook: "한국 및 해외 시장에서 LAMKO 전자재료·디스플레이 포트폴리오의 사업 성장을 이끌고, 주요 고객사와 대리점을 관리합니다.",
    chips: ["B2B 특수/전자 소재 경력 5년 이상", "한국어 원어민, 비즈니스 영어", "국내외 출장"],
    more: [
      "주요 고객사 및 대리점 관계를 구축·관리하고, 한국·APAC·북미 시장에서 신규 사업을 개발합니다.",
      "고객사와의 신제품 도입 및 품질 인증을 주도하며, 샘플·문서·일정을 공급 및 기술 팀과 조율합니다.",
      "SEMICON, K-Display 등 국내외 전시회에서 회사를 대표하고, 신규 시장 진출 전략을 수립합니다.",
      "화장품 활성 성분 라인의 대리점 관계를 총괄하며, 추가 제품 영역으로 확장할 수 있습니다.",
      "우대: 기존 고객·대리점 네트워크, 이원화/국산화 공급 전략 경험, 일본어 또는 중국어, MBA 또는 이공계 학위.",
      "보수: 경력에 따른 경쟁력 있는 연봉, 성과급, 4대 보험. 차량 유류비 지원.",
    ],
  },
  {
    id: "Sales Executive",
    title: "영업 담당자 (Sales Executive)",
    tag: "서울 · 정규직",
    hook: "영업·사업개발 매니저에게 보고하며, 한국 시장의 일상 영업과 고객 지원을 담당합니다.",
    chips: ["B2B 영업 경력 3년 이상", "한국어 원어민 필수, 영어 불필요", "서울 근무, 국내 출장"],
    more: [
      "문의 대응, 견적, 후속 관리 등 기존 고객에 대한 일상적인 한국어 응대를 담당합니다.",
      "신규 리드와 웹사이트 문의를 후속 관리하여 수주로 연결합니다.",
      "고객과 내부 팀 간 샘플, 문서, 납기 일정을 조율합니다.",
      "고객 방문, 전시회, 업계 행사를 지원하고 주간 업무 보고를 수행합니다.",
      "우대: 특수/전자 소재 분야의 기존 고객 기반 또는 대리점 인맥, 이공계 배경.",
      "보수: 연봉 3,500만~4,000만원(경력에 따라 협의), 4대 보험.",
    ],
  },
];

function ApplyForm({ role, L, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const cv = e.target.elements.cv.files[0];
    const cover = e.target.elements.cover.files[0];
    const extra = e.target.elements.extra.files[0];
    if (!name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setErr(L.errName); return; }
    if (!cv) { setErr(L.errCv); return; }
    const okType = (f) => !f || /\.(pdf|docx?)$/i.test(f.name);
    if (!okType(cv) || !okType(cover) || !okType(extra)) { setErr(L.errType); return; }
    const okSize = (f) => !f || f.size <= 8388608;
    if (!okSize(cv) || !okSize(cover) || !okSize(extra)) { setErr(L.errSize); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", email.trim());
      fd.append("phone", phone.trim());
      fd.append("role", role);
      fd.append("message", msg.trim());
      fd.append("cv", cv);
      if (cover) fd.append("cover", cover);
      if (extra) fd.append("extra", extra);
      const r = await fetch(API + "/api/careers/apply", { method: "POST", body: fd });
      const j = await r.json();
      if (j && j.ok) setDone(true);
      else setErr((j && j.error) || L.errFail);
    } catch (x) {
      setErr(L.errFail);
    }
    setBusy(false);
  }

  if (done) {
    return (
      <div style={{ marginTop: 14, background: "#0f2417", border: "1px solid #14532d", borderRadius: 8, padding: 14 }}>
        <p style={{ ...S.p, color: "#86efac", marginBottom: 6 }}><strong>{L.successTitle}</strong></p>
        <p style={S.note}>{L.successNote}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 14, borderTop: "1px solid #1f2a44", paddingTop: 14 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{L.applyTitle}: {role}</div>
      <label style={S.label}>{L.name}</label>
      <input style={S.input} value={name} onChange={(e) => setName(e.target.value)} />
      <label style={S.label}>{L.email}</label>
      <input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label style={S.label}>{L.phone}</label>
      <input style={S.input} value={phone} onChange={(e) => setPhone(e.target.value)} />
      <label style={S.label}>{L.cover}</label>
      <textarea style={{ ...S.input, minHeight: 90, resize: "vertical" }} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={L.coverPh} />
      <label style={S.label}>{L.cv}</label>
      <input style={S.input} type="file" name="cv" accept=".pdf,.doc,.docx" />
      <label style={S.label}>{L.coverFile}</label>
      <input style={S.input} type="file" name="cover" accept=".pdf,.doc,.docx" />
      <label style={S.label}>{L.materials}</label>
      <input style={S.input} type="file" name="extra" accept=".pdf,.doc,.docx" />
      {err ? <p style={{ ...S.note, color: "#fca5a5" }}>{err}</p> : null}
      <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
        <button type="submit" style={S.btn} disabled={busy}>{busy ? L.submitting : L.submit}</button>
        <button type="button" style={S.ghost} onClick={onClose}>{L.cancel}</button>
      </div>
      <p style={S.note}>{L.note}</p>
    </form>
  );
}

export default function Careers() {
  const { i18n } = useTranslation();
  const lang = ((i18n && i18n.language) || "en").slice(0, 2);
  const L = UI[lang] || UI.en;
  const roles = lang === "ko" ? ROLES_KO : ROLES_EN;
  const [applying, setApplying] = useState("");
  return (
    <div style={S.wrap}>
      <SEO title="Careers" description="Open positions at LAMKO Co., Ltd. in Seoul. Join a team building high-purity materials for the semiconductor, display and specialty chemical industries." path="/careers" />
      <div style={S.hero}>
        <div style={{ fontSize: 13, letterSpacing: 2, color: "#fdba74", fontWeight: 700, marginBottom: 10 }}>{L.kicker}</div>
        <h1 style={S.h1}>{L.title}</h1>
        <p style={S.lead}>{L.lead}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{L.open}</h2>
        {roles.map((r) => (
          <div key={r.id} style={S.card}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{r.title}</div>
            <div style={{ fontSize: 13, color: "#94a3b8", margin: "4px 0 10px" }}>{r.tag}</div>
            <p style={S.p}>{r.hook}</p>
            <div style={{ marginBottom: 6 }}>{r.chips.map((c) => (<span key={c} style={S.chip}>{c}</span>))}</div>
            <details>
              <summary style={{ cursor: "pointer", color: "#fdba74", fontSize: 14, fontWeight: 600 }}>{L.details}</summary>
              <ul style={{ paddingLeft: 18, marginTop: 10 }}>
                {r.more.map((m, i) => (<li key={i} style={S.li}>{m}</li>))}
              </ul>
            </details>
            {applying === r.id ? (
              <ApplyForm role={r.id} L={L} onClose={() => setApplying("")} />
            ) : (
              <div style={{ marginTop: 12 }}>
                <button type="button" style={S.btn} onClick={() => setApplying(r.id)}>{L.apply}</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{L.working}</h2>
        <div style={S.card}>
          <p style={S.p}>{L.workingP1}</p>
          <p style={S.p}>{L.workingP2}</p>
        </div>
      </div>

      <div style={{ ...S.section, paddingBottom: 80 }}>
        <h2 style={S.h2}>{L.process}</h2>
        <p style={S.p}>{L.processText}</p>
        <p style={S.note}>{L.spam}</p>
      </div>
    </div>
  );
}
